import React, { useState } from 'react';
import { useLanguage } from '../../contexts/language';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '../../integrations/supabase/client';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { City } from '../../data/types/city-types';

interface DonationFormProps {
  city: City;
  onCancel: () => void;
}

const pledgeSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  email: z.string().email({ message: 'Please enter a valid email' }),
  amount: z.coerce.number().min(5, { message: 'Minimum pledge is $5' }),
});

const DonationForm: React.FC<DonationFormProps> = ({ city, onCancel }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof pledgeSchema>>({
    resolver: zodResolver(pledgeSchema),
    defaultValues: {
      name: '',
      email: '',
      amount: 50,
    },
  });
  
  const handlePledge = async (values: z.infer<typeof pledgeSchema>) => {
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          regionId: city.region_id,
          regionName: city.name,
          cityId: city.city_id,
          amount: values.amount,
          donorName: values.name,
          donorEmail: values.email,
          donationType: 'city'
        },
      });
      
      if (error) throw error;
      
      // Redirect to Stripe Checkout
      window.location.href = data.sessionUrl;
    } catch (error) {
      console.error('Error creating pledge session:', error);
      toast({
        title: t('pledgeError'),
        description: t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold mb-4">{t('makePledge')}</h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('pledgeDescription', { city: city.name })}
      </p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handlePledge)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('name')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('enterName')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('email')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('enterEmail')} type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('donationAmount')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                    <Input type="number" className="pl-7" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="flex gap-3 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={onCancel}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('processing') : t('pledge')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default DonationForm;
